import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { getDirectUserMembersOfUserGroups } from "client/Api/Communities/community";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import {
	FormItem,
	SearchInput,
	SearchPlaceholder,
	Toolbar
} from "client/Components";
import { SelectUserAndUserGroupDialog } from "client/Features/Users/UserAndUserGroupCollection/SelectUserAndUserGroupDialog";
import { UserAndUserGroupKind } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { UserAndUserGroupSelection } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupOverviews";
import { UserCollection, UserCollectionMode } from "client/Features/Users/UserAndUserGroupCollection/UserCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { ICollectionInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./Members.module.less";
import { isNil } from "client/Misc/utils";

export type MembersProps = {
	attachable: ICollectionInputAttachable<number, readonly number[]>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	nonDeletableMembersIds: readonly number[];
	isEditable: boolean;
};

export const Members = (props: MembersProps): ReactElement => {
	const {
		attachable,
		usersAndUserGroupsData,
		nonDeletableMembersIds,
		isEditable
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [addUsersDialogState, setAddUsersDialogState] = useState<AddUsersDialogState>({ isOpen: false });

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const mode = useMemo(
		(): UserCollectionMode => {
			if (!isEditable) {
				return {
					kind: UserAndUserGroupKind.Read
				};
			}

			return {
				kind: UserAndUserGroupKind.Edit,
				onChange: (userIds: readonly number[]): void => {
					attachable
						.reducer
						.setValue(userIds);

					attachable
						.validation
						.triggerValueChanged();
				},
				getCanBeDeleted: (userId: number): boolean => {
					return nonDeletableMembersIds.indexOf(userId) === -1;
				}
			};
		},
		[
			attachable,
			isEditable,
			nonDeletableMembersIds
		]
	);

	const selectHandler = useCallback(
		async (selection: UserAndUserGroupSelection): Promise<void> => {
			setAddUsersDialogState({
				isOpen: true,
				isBusy: true
			});

			const allSelectedUserIds: number[] = [
				...selection.users
					.map(u => u.id)
			];

			try {
				if (selection.userGroups.length > 0) {
					const memberOfSelectedGroups: readonly UserGridItem[] = await getDirectUserMembersOfUserGroups(
						selection.userGroups
							.map(g => g.id),
						globalCancellation
					);

					allSelectedUserIds.push(
						...memberOfSelectedGroups
							// Simulate SelectUserAndUserGroupDialog where deleted users are not up for selection
							.filter((u) => isNil(u.deletionDate))
							.map(x => x.id)
					);
				}

				attachable
					.reducer
					.setValue(
						unique([
							...attachable
								.value,
							...allSelectedUserIds
						])
					);

				attachable
					.validation
					.triggerValueChanged();

				setAddUsersDialogState({ isOpen: false });
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setAddUsersDialogState({
					isOpen: true,
					isBusy: false
				});

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			alertManager,
			attachable,
			globalCancellation
		]
	);

	return (
		<>
			{isEditable && (
				<div className={styles.buttonList}>
					<Toolbar>
						<Toolbar.Button
							trackingId="add-users-button"
							kind="secondary"
							icon="plus"
							text={localize("_AddUsers")}
							onClick={() => setAddUsersDialogState({
								isOpen: true,
								isBusy: false
							})}
						/>
					</Toolbar>
				</div>
			)}
			<div className={styles.searchInputContainer}>
				<SearchInput
					trackingId="search"
					onChange={setSearchTerm}
					icon="filter"
					height="large"
					placeholder={
						<SearchPlaceholder
							kind="filter"
							properties={[
								localize("_Name")
							]}
						/>
					}
				/>
			</div>
			<FormItem height="stretch">
				<UserCollection
					height="fill"
					allData={usersAndUserGroupsData.users}
					mode={mode}
					searchTerm={searchTerm}
					value={attachable.value}
				/>
			</FormItem>

			{addUsersDialogState.isOpen && (
				<SelectUserAndUserGroupDialog
					data={usersAndUserGroupsData}
					initialActiveTab={ActiveTab.User}
					title={localize("_AddUsers")}
					onClose={() => setAddUsersDialogState({ isOpen: false })}
					onSelected={selectHandler}
					hasBusyIndicator={addUsersDialogState.isBusy}
				/>
			)}
		</>
	);
};
Members.displayName = "Members";

type AddUsersDialogState = {
	isOpen: true;
	isBusy: boolean;
} | {
	isOpen: false;
	isBusy?: undefined;
};